import { Package, MapPin, Check, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatPrice } from "@/lib/formatPrice";
import { cn } from "@/lib/utils";

interface OrderItem {
  id: number;
  product_id: number;
  product_name: string;
  product_sku: string;
  product_bin_location: string | null;
  product_image: string | null;
  price: number;
}

interface PackingListProps {
  items: OrderItem[];
  packedItems: Set<number>;
  onTogglePacked: (itemId: number) => void;
  onMarkAllPacked: () => void;
  allPacked: boolean;
  orderStatus: string;
  onFulfill: () => void;
  isFulfilling: boolean;
}

export default function PackingList({
  items,
  packedItems,
  onTogglePacked,
  onMarkAllPacked,
  allPacked,
  orderStatus,
  onFulfill,
  isFulfilling,
}: PackingListProps) {
  return (
    <div className="bg-white rounded-xl border border-border/60 overflow-hidden">
      <div className="px-5 py-3 border-b border-border/40 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Package className="size-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Pakovanje
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {packedItems.size}/{items.length} spakovano
          </span>
          {!allPacked && (
            <button
              onClick={onMarkAllPacked}
              className="text-xs text-foreground/70 hover:text-foreground underline transition-colors"
            >
              Označi sve
            </button>
          )}
        </div>
      </div>

      <div className="divide-y divide-border/40">
        {items.map((item) => {
          const isPacked = packedItems.has(item.id);
          return (
            <div
              key={item.id}
              className={cn(
                "flex items-center gap-3 sm:gap-4 px-5 py-3 transition-colors",
                isPacked && "bg-emerald-50/50"
              )}
            >
              <button
                onClick={() => onTogglePacked(item.id)}
                className={cn(
                  "size-7 rounded-lg border-2 flex items-center justify-center shrink-0 transition-all cursor-pointer",
                  isPacked
                    ? "bg-emerald-500 border-emerald-500 text-white"
                    : "border-border hover:border-foreground/40"
                )}
              >
                {isPacked && <Check className="size-4" />}
              </button>

              {item.product_image ? (
                <img
                  src={`/uploads/${item.product_id}-thumb.webp`}
                  alt={item.product_name}
                  className="size-12 rounded-lg object-cover shrink-0 bg-muted"
                />
              ) : (
                <div className="size-12 rounded-lg bg-muted flex items-center justify-center text-muted-foreground text-xs shrink-0">
                  N/A
                </div>
              )}

              <div className="flex-1 min-w-0">
                <p className={cn("text-sm font-medium truncate", isPacked && "line-through text-muted-foreground")}>
                  {item.product_name}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">{item.product_sku}</p>
              </div>

              <div className="flex items-center gap-1.5 shrink-0">
                {item.product_bin_location ? (
                  <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 rounded-md px-2 py-1 text-xs font-mono font-semibold">
                    <MapPin className="size-3" />
                    {item.product_bin_location}
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground/50">—</span>
                )}
              </div>

              <span className="text-sm font-medium tabular-nums shrink-0">
                {formatPrice(item.price)} KM
              </span>
            </div>
          );
        })}
      </div>

      {allPacked && (
        <div className="px-5 py-3 bg-emerald-50 border-t border-emerald-100 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Check className="size-4 text-emerald-600" />
            <span className="text-sm font-medium text-emerald-700">Svi artikli spakovani</span>
          </div>
          {(orderStatus === "new" || orderStatus === "processing") && (
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={isFulfilling}
              onClick={onFulfill}
            >
              {isFulfilling ? "Ažuriranje..." : "Isporuči"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
